<template>
    <div class="secure-sign__container">
        <v-container>
            <s-s-form-header
                title="Denzapine Monitoring Service Patient Discontinuation Form"
                document-number="PV01F01"
                version="5.0"
                effective-date="29 Feb 2024"
                review-date="28 Feb 2026"
                supersedes="4.0"
            ></s-s-form-header>
            <v-col class="text-center">
                <h1 class="mb-5">Denzapine Monitoring Service Patient Discontinuation Form</h1>
                <h4 class="mb-8">
                    Please complete all fields before submission of this form. Missing information may lead to delays in
                    processing the patient discontinuation.
                </h4>
            </v-col>
            <v-col cols="12">
                <v-form ref="form" v-model="isFormValid" :disabled="isDisabled">
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Pharmacy Details</h5>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.pharmacyName"
                                label="Pharmacy Name:"
                                :rules="validationRules.pharmacyName"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Patient Details</h5>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.patientDmsNumber"
                                label="Patient DMS Number:"
                                :rules="validationRules.patientDmsNumber"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.patientFirstName"
                                label="Patient First Name:"
                                :rules="validationRules.patientFirstName"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.patientLastName"
                                label="Patient Last Name:"
                                :rules="validationRules.patientLastName"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.dateDenzapineStopped"
                                label="Date Denzapine® Stopped:"
                                :rules="validationRules.dateDenzapineStopped"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-textarea
                                label="Reason for Discontinuation:"
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.discontinuation"
                                :rules="validationRules.discontinuation"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.bloodMonitoringDate"
                                :rules="validationRules.bloodMonitoringDate"
                                label="Date(s) Follow-up Blood Monitoring Expected"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Consultant / Pharmacist Signature</h5>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.printName"
                                :rules="validationRules.printName"
                                label="Print Name:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                hide-details
                                to-mutate="secure_sign_forms::patientDiscontinuationForm.date"
                                label="Date"
                                :rules="validationRules.date"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="8">
                            <secure-sign-form-signature-component
                                ref="signaturePad"
                                :form-type="form"
                                :is-disabled="isDisabled"
                            ></secure-sign-form-signature-component>
                        </v-col>
                        <v-col
                            v-if="!isDisabled"
                            cols="12"
                            sm="4"
                            class="d-flex flex-column justify-content-end align-items-center"
                        >
                            <secure-sign-form-recaptcha
                                ref="recaptcha"
                                :valid-recaptcha="validRecaptcha"
                                :is-disabled="isDisabled"
                                @expired="expired"
                                @verify="onRecaptchaVerify"
                            ></secure-sign-form-recaptcha>
                            <v-btn
                                :loading="loading"
                                class="mt-2"
                                max-width="304px"
                                width="100%"
                                size="x-large"
                                color="primary"
                                :disabled="isDisabled"
                                @click="submit"
                                >Submit
                            </v-btn>
                        </v-col>
                        <v-col cols="12" class="text-center font-weight-bold">
                            <p>
                                Please Note: Monitoring should continue at the current frequency for one month after
                                discontinuation of Denzapine
                            </p>
                        </v-col>
                    </v-row>
                    <s-s-form-footer ref="footer">
                        <p>
                            Should you have any queries, please do not hesitate to contact the Denzapine Monitoring
                            Service Team on 0333 200 4141
                        </p>
                        <p>Job Code: UK-Denz-2300029, Version 2.0, Date of prep: Apr 2026</p>
                    </s-s-form-footer>
                </v-form>
            </v-col>
        </v-container>
    </div>
</template>

<script>
import SSFormFooter from './partials/SSFormFooter.vue';
import SSFormHeader from './partials/SSFormHeader.vue';

let formControl = require('./mixins/FormControl.js').default;
export default {
    components: { SSFormHeader, SSFormFooter },
    mixins: [formControl],
    data() {
        return {
            validationRules: {
                pharmacyName: [(v) => !!v || t('users::validation.field.required')],
                patientDmsNumber: [(v) => !!v || t('users::validation.field.required')],
                patientFirstName: [(v) => !!v || t('users::validation.field.required')],
                patientLastName: [(v) => !!v || t('users::validation.field.required')],
                dateDenzapineStopped: [(v) => !!v || t('users::validation.field.required')],
                discontinuation: [(v) => !!v || t('users::validation.field.required')],
                bloodMonitoringDate: [(v) => !!v || t('users::validation.field.required')],
                printName: [(v) => !!v || t('users::validation.field.required')],
                date: [(v) => !!v || t('users::validation.field.required')],
            },
            isFormValid: false,
            formType: 'patient-discontinuation-form',
            form: 'patientDiscontinuationForm',
            isSignatureValid: false,
        };
    },
    mounted() {
        this.getFormData();
    },
};
</script>

<style></style>
